import { CSSProperties, ReactNode, useEffect, useState } from "react";
import "./DocumentScanner.css";

export type DocumentScannerStatus = "loading" | "success" | "error";
export type DocumentScannerSize = "sm" | "md" | "lg";

export interface DocumentScannerStage {
  label: string;
  icon?: ReactNode;
}

export interface DocumentScannerProps {
  status?: DocumentScannerStatus;
  size?: DocumentScannerSize;
  fluid?: boolean;
  stages?: DocumentScannerStage[];
  stageDuration?: number;
  successLabel?: string;
  errorLabel?: string;
  showLabel?: boolean;
  color?: string;
  background?: string;
  className?: string;
  style?: CSSProperties;
}

const DocIcon = () => (
  <svg className="ds-icon-doc" viewBox="0 0 24 24">
    <path d="M14 3 H7 a2 2 0 0 0 -2 2 v14 a2 2 0 0 0 2 2 h10 a2 2 0 0 0 2 -2 V8 z" />
    <path d="M14 3 v5 h5" />
    <path className="ds-doc-line" d="M9 13 h6" />
    <path className="ds-doc-line" d="M9 17 h4" />
  </svg>
);

const SearchIcon = () => (
  <svg className="ds-icon-search" viewBox="0 0 24 24">
    <g className="ds-search-circle">
      <circle cx="11" cy="11" r="7" />
    </g>
    <path d="M20 20 l-3.5 -3.5" />
    <path d="M8.5 11 l2 2 l3 -3.5" />
  </svg>
);

const UserCheckIcon = () => (
  <svg className="ds-icon-user" viewBox="0 0 24 24">
    <path d="M16 21 v-2 a4 4 0 0 0 -4 -4 H7 a4 4 0 0 0 -4 4 v2" />
    <circle cx="9.5" cy="7" r="4" />
    <path d="M16 11 l2 2 l4 -4" />
  </svg>
);

const DEFAULT_STAGES: DocumentScannerStage[] = [
  { label: "Reading document", icon: <DocIcon /> },
  { label: "Detecting fields", icon: <SearchIcon /> },
  { label: "Verifying identity", icon: <UserCheckIcon /> },
];

export function DocumentScanner({
  status = "loading",
  size = "md",
  fluid = false,
  stages = DEFAULT_STAGES,
  stageDuration = 2,
  successLabel = "Identidad verificada",
  errorLabel = "No se pudo verificar",
  showLabel = true,
  color,
  background,
  className,
  style,
}: DocumentScannerProps) {
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState(-1);

  useEffect(() => {
    if (status !== "loading" || stages.length <= 1) return;
    const id = window.setInterval(() => {
      setActive((curr) => {
        setPrev(curr);
        return (curr + 1) % stages.length;
      });
    }, Math.max(0.5, stageDuration) * 1000);
    return () => window.clearInterval(id);
  }, [status, stages.length, stageDuration]);

  useEffect(() => {
    if (status !== "loading") {
      setActive(0);
      setPrev(-1);
    }
  }, [status]);

  const cssVars: CSSProperties = {
    ...(color ? ({ ["--ds-color" as never]: color } as CSSProperties) : {}),
    ...(background ? ({ ["--ds-bg" as never]: background } as CSSProperties) : {}),
    ...style,
  };

  const cardClass = ["ds-card", status === "success" && "ds-success", status === "error" && "ds-error"]
    .filter(Boolean)
    .join(" ");

  const rootClass = ["ds-root", `ds-${size}`, fluid && "ds-fluid", className].filter(Boolean).join(" ");

  const stageClass = (i: number) => {
    if (i === active) return "ds-stage-item is-active";
    if (i === prev) return "ds-stage-item is-prev";
    return "ds-stage-item";
  };

  return (
    <div className={rootClass} style={cssVars} role="status" aria-live="polite">
      <div className={cardClass}>
        <div className="ds-corners">
          <span /><span /><span /><span />
        </div>

        <div className="ds-avatar" />

        <div className="ds-info">
          <span /><span /><span /><span />
        </div>

        <div className="ds-particles">
          <span /><span /><span /><span /><span />
        </div>

        <div className="ds-beam" />

        <div className="ds-icon ds-icon-success" aria-hidden={status !== "success"}>
          <svg viewBox="0 0 60 60">
            <circle cx="30" cy="30" r="26" />
            <path d="M18 30 L27 39 L42 22" />
          </svg>
        </div>

        <div className="ds-icon ds-icon-error" aria-hidden={status !== "error"}>
          <svg viewBox="0 0 60 60">
            <circle cx="30" cy="30" r="26" />
            <path d="M22 22 L38 38 M38 22 L22 38" />
          </svg>
        </div>
      </div>

      {showLabel && status === "loading" && stages.length > 0 && (
        <div className="ds-stages">
          <div className="ds-stage-icon-wrap">
            {stages.map((stage, i) => (
              <div key={i} className={stageClass(i)}>{stage.icon}</div>
            ))}
          </div>
          <div className="ds-stage-text-wrap">
            {stages.map((stage, i) => (
              <div key={i} className={stageClass(i)}>
                <span className="ds-stage-text">
                  {stage.label}
                  <span className="ds-dots" />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

export default DocumentScanner;
